import { useMemo, useState } from "react";
import "./css/useDalgoTOperationsConsole.scss";
import DalgoTBrightness, { useSeoulSunset } from "./dalgoTBrightness";

interface VehicleSetting {
  id: number;
  name: string;
  lidarEnabled: boolean;
  sunsetTime: string;
  autoBrightnessEnabled: boolean;
}

const initialVehicleSettings: VehicleSetting[] = Array.from(
  { length: 4 },
  (_, index) => ({
    id: index + 1,
    name: `dalgoT ${index + 1}호차`,
    lidarEnabled: true,
    sunsetTime: "18:30",
    autoBrightnessEnabled: true,
  }),
);

const DalgoTOperationsConsole = () => {
  const [vehicleSettings, setVehicleSettings] = useState(
    initialVehicleSettings,
  );
  const { sunset } = useSeoulSunset();
  const sunsetTime = `${String(Math.floor(sunset / 60)).padStart(2, "0")}:${String(sunset % 60).padStart(2, "0")}`;

  const enabledCounts = useMemo(
    () => ({
      lidar: vehicleSettings.filter((vehicle) => vehicle.lidarEnabled).length,
      autoBrightness: vehicleSettings.filter(
        (vehicle) => vehicle.autoBrightnessEnabled,
      ).length,
    }),
    [vehicleSettings],
  );

  const toggleSetting = (
    vehicleId: number,
    key: "lidarEnabled" | "autoBrightnessEnabled",
  ) => {
    setVehicleSettings((currentSettings) =>
      currentSettings.map((vehicle) =>
        vehicle.id === vehicleId
          ? { ...vehicle, [key]: !vehicle[key] }
          : vehicle,
      ),
    );
  };

  return (
    <section id="dalgo-t-operations-console">
      <header className="dalgo-console__header">
        <div>
          <span className="dalgo-console__eyebrow">DALGO T · OPERATIONS</span>
          <h1>dalgoT 운영관제</h1>
          <p>차량별 차량근접과 자동 밝기 설정 상태를 확인합니다.</p>
        </div>
        <div className="dalgo-console__mode">
          <i aria-hidden="true" />
          화면 설정 모드
        </div>
      </header>

      <div className="dalgo-console__summary" aria-label="차량 설정 요약">
        <article>
          <span>운영 차량</span>
          <strong>
            {vehicleSettings.length}
            <small>대</small>
          </strong>
        </article>
        <article className="is-lidar">
          <span>차량근접 ON</span>
          <strong>
            {enabledCounts.lidar}
            <small>대</small>
          </strong>
        </article>
        <article className="is-brightness">
          <span>자동 밝기 ON</span>
          <strong>
            {enabledCounts.autoBrightness}
            <small>대</small>
          </strong>
        </article>
      </div>

      <section className="dalgo-console__panel" aria-label="dalgoT 차량 설정">
        <header className="dalgo-console__panel-header">
          <div>
            <strong>차량 설정 정보</strong>
            <span>토글 변경은 현재 화면에서만 유지됩니다.</span>
          </div>
          <span>4 VEHICLES</span>
        </header>

        <div className="dalgo-console__table-wrap">
          <table>
            <thead>
              <tr>
                <th>차량</th>
                <th>차량근접</th>
                <th>일몰시간</th>
                <th>자동 밝기 조절</th>
              </tr>
            </thead>
            <tbody>
              {vehicleSettings.map((vehicle) => (
                <tr key={vehicle.id}>
                  <td>
                    <div className="dalgo-console__vehicle">
                      <span>{String(vehicle.id).padStart(2, "0")}</span>
                      <div>
                        <strong>{vehicle.name}</strong>
                        <small>DALGO T VEHICLE</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={vehicle.lidarEnabled}
                      aria-label={`${vehicle.name} 차량근접 ${vehicle.lidarEnabled ? "끄기" : "켜기"}`}
                      className={`dalgo-console__toggle${vehicle.lidarEnabled ? " is-on" : ""}`}
                      onClick={() => toggleSetting(vehicle.id, "lidarEnabled")}
                    >
                      <i aria-hidden="true" />
                      <span>{vehicle.lidarEnabled ? "ON" : "OFF"}</span>
                    </button>
                  </td>
                  <td>
                    <div className="dalgo-console__sunset">
                      <span aria-hidden="true">◐</span>
                      <div>
                        <time>{sunsetTime}</time>
                        <small>SEOUL SUNSET</small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={vehicle.autoBrightnessEnabled}
                      aria-label={`${vehicle.name} 자동 밝기 조절 ${vehicle.autoBrightnessEnabled ? "끄기" : "켜기"}`}
                      className={`dalgo-console__toggle${vehicle.autoBrightnessEnabled ? " is-on" : ""}`}
                      onClick={() =>
                        toggleSetting(vehicle.id, "autoBrightnessEnabled")
                      }
                    >
                      <i aria-hidden="true" />
                      <span>{vehicle.autoBrightnessEnabled ? "ON" : "OFF"}</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <DalgoTBrightness vehicles={vehicleSettings} />
    </section>
  );
};

export default DalgoTOperationsConsole;
